/**
 * User Communication Service
 *
 * Provides user notification functionality for theme announcements and system updates.
 * Integrates with existing emailNotificationService and Supabase edge functions.
 *
 * Key features:
 * - Send theme announcement emails using Supabase edge functions
 * - Create in-app notifications stored in user_notifications table
 * - Bulk email sending for all users
 * - Mark notifications as read
 * - Fetch unread notifications
 */

import { supabase } from '@/integrations/supabase/client';

// ============================================================================
// Types
// ============================================================================

/**
 * Notification types supported by the system
 */
export type NotificationType = 'info' | 'warning' | 'announcement' | 'success';

/**
 * User notification record from the database
 */
export interface UserNotification {
  id: number;
  user_id: string;
  type: NotificationType;
  title: string;
  message: string;
  is_read: boolean;
  created_at: string;
  expires_at: string | null;
}

/**
 * Theme announcement email data
 */
export interface ThemeAnnouncementData {
  userName: string;
  rolloutDate: Date;
  appUrl: string;
  unsubscribeUrl: string;
}

/**
 * Result of bulk email sending operation
 */
export interface BulkSendResult {
  success: number;
  failed: number;
  errors: Array<{ email: string; error: string }>;
}

// ============================================================================
// Constants
// ============================================================================

const THEME_ANNOUNCEMENT_TEMPLATE = '/src/templates/email/theme-change-announcement.html';
const SUPABASE_EMAIL_FUNCTION = 'send-contact-email'; // Using existing email function
const DISMISSED_NOTIFICATIONS_KEY = 'dismissed_notifications';

// ============================================================================
// Email Sending Functions
// ============================================================================

/**
 * Sends a theme announcement email to a single user
 * Uses the existing Supabase edge function for email sending
 *
 * @param userId - The user's ID
 * @param userEmail - The user's email address
 * @param rolloutDate - The date when the rollout begins
 * @returns Promise<boolean> - True if email was sent successfully
 */
export async function sendThemeAnnouncement(
  userId: string,
  userEmail: string,
  rolloutDate: Date
): Promise<boolean> {
  try {
    // Format the rollout date for Portuguese locale
    const formattedDate = rolloutDate.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });

    // Get user's name from profiles table
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('full_name, email')
      .eq('id', userId)
      .single();

    const userName = profile?.full_name || 'Usuario';

    // Prepare email data
    const emailData = {
      name: userName,
      email: userEmail,
      subject: 'Atualizacao visual no modo escuro - O que voce precisa saber',
      message: `Estamos melhorando a experiencia do modo escuro com cores mais confortaveis. A mudanca comeca em ${formattedDate}.`
    };

    // Call the existing Supabase edge function for email sending
    const { data, error } = await supabase.functions.invoke(SUPABASE_EMAIL_FUNCTION, {
      body: emailData
    });

    if (error) {
      console.error('Error sending theme announcement email:', error);
      return false;
    }

    console.log('Theme announcement email sent to:', userEmail);
    return true;
  } catch (error) {
    console.error('Unexpected error sending theme announcement:', error);
    return false;
  }
}

/**
 * Sends theme announcement emails to multiple users (bulk operation)
 * Processes emails in batches to avoid overwhelming the email service
 *
 * @param userIds - Array of user IDs and their email addresses
 * @param rolloutDate - The date when the rollout begins
 * @returns Promise<BulkSendResult> - Summary of send operation
 */
export async function sendBulkAnnouncement(
  userIds: Array<{ id: string; email: string }>,
  rolloutDate: Date
): Promise<BulkSendResult> {
  const result: BulkSendResult = {
    success: 0,
    failed: 0,
    errors: []
  };

  // Process in batches of 10 to avoid rate limiting
  const batchSize = 10;
  for (let i = 0; i < userIds.length; i += batchSize) {
    const batch = userIds.slice(i, i + batchSize);

    // Send emails in parallel within the batch
    await Promise.all(
      batch.map(async ({ id, email }) => {
        const success = await sendThemeAnnouncement(id, email, rolloutDate);
        if (success) {
          result.success++;
        } else {
          result.failed++;
          result.errors.push({ email, error: 'Failed to send email' });
        }
      })
    );

    // Small delay between batches to respect rate limits
    if (i + batchSize < userIds.length) {
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
  }

  return result;
}

/**
 * Sends theme announcement to all active users in the system
 *
 * @param rolloutDate - The date when the rollout begins
 * @returns Promise<BulkSendResult> - Summary of send operation
 */
export async function sendAnnouncementToAllUsers(
  rolloutDate: Date
): Promise<BulkSendResult> {
  try {
    // Fetch all users with their emails from auth.users and profiles
    const { data: users, error } = await supabase
      .from('profiles')
      .select('id, email')
      .not('email', 'is', null);

    if (error) {
      console.error('Error fetching users for bulk announcement:', error);
      return { success: 0, failed: 0, errors: [] };
    }

    const userIds = (users || []).map(u => ({ id: u.id, email: u.email as string }));
    return await sendBulkAnnouncement(userIds, rolloutDate);
  } catch (error) {
    console.error('Unexpected error in sendAnnouncementToAllUsers:', error);
    return { success: 0, failed: 0, errors: [] };
  }
}

// ============================================================================
// In-App Notification Functions
// ============================================================================

/**
 * Creates an in-app notification for a user
 * Stores the notification in the user_notifications table
 *
 * @param userId - The user's ID
 * @param type - The notification type
 * @param title - Short title for the notification
 * @param message - Full message content
 * @param expiresAt - Optional expiration date
 * @returns Promise<void>
 */
export async function createInAppNotification(
  userId: string,
  type: NotificationType,
  title: string,
  message: string,
  expiresAt?: Date
): Promise<void> {
  try {
    const { error } = await supabase
      .from('user_notifications')
      .insert({
        user_id: userId,
        type,
        title,
        message,
        expires_at: expiresAt ? expiresAt.toISOString() : null
      });

    if (error) {
      console.error('Error creating in-app notification:', error);
      throw error;
    }
  } catch (error) {
    console.error('Unexpected error creating in-app notification:', error);
    throw error;
  }
}

/**
 * Creates a bulk in-app notification for all users
 * Uses the Supabase function for efficient bulk creation
 *
 * @param type - The notification type
 * @param title - Short title for the notification
 * @param message - Full message content
 * @param expiresAt - Optional expiration date
 * @returns Promise<number> - Number of notifications created
 */
export async function createBulkInAppNotification(
  type: NotificationType,
  title: string,
  message: string,
  expiresAt?: Date
): Promise<number> {
  try {
    const { data, error } = await supabase.rpc('create_bulk_announcement', {
      notification_type: type,
      notification_title: title,
      notification_message: message,
      notification_expires_at: expiresAt ? expiresAt.toISOString() : null
    });

    if (error) {
      console.error('Error creating bulk in-app notification:', error);
      return 0;
    }

    return data as number || 0;
  } catch (error) {
    console.error('Unexpected error creating bulk in-app notification:', error);
    return 0;
  }
}

/**
 * Creates both email and in-app notification for theme announcement
 * This is the main function to use for announcing theme changes
 *
 * @param userId - The user's ID
 * @param userEmail - The user's email address
 * @param rolloutDate - The date when the rollout begins
 * @param createInApp - Whether to also create in-app notification (default: true)
 * @returns Promise<boolean> - True if all notifications were created successfully
 */
export async function sendThemeAnnouncementWithNotification(
  userId: string,
  userEmail: string,
  rolloutDate: Date,
  createInApp: boolean = true
): Promise<boolean> {
  try {
    // Send email notification
    const emailSent = await sendThemeAnnouncement(userId, userEmail, rolloutDate);

    // Create in-app notification if requested
    if (createInApp) {
      const formattedDate = rolloutDate.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });

      await createInAppNotification(
        userId,
        'announcement',
        'Nova aparencia do modo escuro',
        `Estamos atualizando o modo escuro com cores mais confortaveis. A mudanca comeca em ${formattedDate}.`,
        rolloutDate // Expires after rollout date
      );
    }

    return emailSent;
  } catch (error) {
    console.error('Error sending theme announcement with notification:', error);
    return false;
  }
}

/**
 * Marks a notification as read
 *
 * @param notificationId - The notification ID
 * @returns Promise<boolean> - True if successfully marked as read
 */
export async function markNotificationAsRead(notificationId: number): Promise<boolean> {
  try {
    // Use the Supabase function for marking as read
    const { data, error } = await supabase.rpc('mark_notification_read', {
      notification_id: notificationId
    });

    if (error) {
      console.error('Error marking notification as read:', error);
      return false;
    }

    return data as boolean || false;
  } catch (error) {
    console.error('Unexpected error marking notification as read:', error);
    return false;
  }
}

/**
 * Marks all unread notifications as read for a user
 *
 * @param userId - The user's ID
 * @returns Promise<boolean> - True if all notifications were marked as read
 */
export async function markAllNotificationsAsRead(userId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('user_notifications')
      .update({ is_read: true })
      .eq('user_id', userId)
      .eq('is_read', false);

    if (error) {
      console.error('Error marking all notifications as read:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Unexpected error marking all notifications as read:', error);
    return false;
  }
}

/**
 * Fetches unread notifications for a user
 *
 * @param userId - The user's ID
 * @returns Promise<UserNotification[]> - Array of unread notifications
 */
export async function getUnreadNotifications(userId: string): Promise<UserNotification[]> {
  try {
    const { data, error } = await supabase
      .from('user_notifications')
      .select('*')
      .eq('user_id', userId)
      .eq('is_read', false)
      .or('expires_at.is.null,expires_at.gt.' + new Date().toISOString())
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching unread notifications:', error);
      return [];
    }

    return (data || []) as UserNotification[];
  } catch (error) {
    console.error('Unexpected error fetching unread notifications:', error);
    return [];
  }
}

/**
 * Fetches all notifications for a user (read and unread)
 *
 * @param userId - The user's ID
 * @param limit - Maximum number of notifications to return (default: 50)
 * @returns Promise<UserNotification[]> - Array of notifications
 */
export async function getAllNotifications(
  userId: string,
  limit: number = 50
): Promise<UserNotification[]> {
  try {
    const { data, error } = await supabase
      .from('user_notifications')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching all notifications:', error);
      return [];
    }

    return (data || []) as UserNotification[];
  } catch (error) {
    console.error('Unexpected error fetching all notifications:', error);
    return [];
  }
}

/**
 * Gets the count of unread notifications for a user
 *
 * @param userId - The user's ID
 * @returns Promise<number> - Count of unread notifications
 */
export async function getUnreadNotificationsCount(userId: string): Promise<number> {
  try {
    const { data, error } = await supabase
      .from('user_unread_notifications_count')
      .select('unread_count')
      .eq('user_id', userId)
      .single();

    if (error) {
      // Fallback to manual count if view fails
      const { count } = await supabase
        .from('user_notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('is_read', false)
        .or('expires_at.is.null,expires_at.gt.' + new Date().toISOString());

      return count || 0;
    }

    return (data?.unread_count as number) || 0;
  } catch (error) {
    console.error('Unexpected error fetching unread notifications count:', error);
    return 0;
  }
}

/**
 * Deletes a notification
 *
 * @param notificationId - The notification ID
 * @param userId - The user's ID (for security check)
 * @returns Promise<boolean> - True if successfully deleted
 */
export async function deleteNotification(
  notificationId: number,
  userId: string
): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('user_notifications')
      .delete()
      .eq('id', notificationId)
      .eq('user_id', userId);

    if (error) {
      console.error('Error deleting notification:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Unexpected error deleting notification:', error);
    return false;
  }
}

// ============================================================================
// Local Storage Helpers for Dismissed Notifications
// ============================================================================

/**
 * Checks if a notification has been dismissed by the user
 * Uses localStorage to persist dismissed state
 *
 * @param notificationId - The notification ID
 * @returns boolean - True if notification was dismissed
 */
export function isNotificationDismissed(notificationId: string | number): boolean {
  if (typeof window === 'undefined') return false;

  try {
    const dismissed = localStorage.getItem(DISMISSED_NOTIFICATIONS_KEY);
    if (!dismissed) return false;

    const dismissedList = JSON.parse(dismissed) as Array<string | number>;
    return dismissedList.includes(String(notificationId));
  } catch {
    return false;
  }
}

/**
 * Marks a notification as dismissed in localStorage
 *
 * @param notificationId - The notification ID
 */
export function dismissNotificationLocally(notificationId: string | number): void {
  if (typeof window === 'undefined') return;

  try {
    const dismissed = localStorage.getItem(DISMISSED_NOTIFICATIONS_KEY);
    let dismissedList: Array<string | number> = dismissed ? JSON.parse(dismissed) : [];

    if (!dismissedList.includes(String(notificationId))) {
      dismissedList.push(String(notificationId));
      localStorage.setItem(DISMISSED_NOTIFICATIONS_KEY, JSON.stringify(dismissedList));
    }
  } catch (error) {
    console.error('Error dismissing notification locally:', error);
  }
}

/**
 * Clears all locally dismissed notifications
 */
export function clearDismissedNotifications(): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(DISMISSED_NOTIFICATIONS_KEY);
  } catch (error) {
    console.error('Error clearing dismissed notifications:', error);
  }
}

// ============================================================================
// Cleanup Functions
// ============================================================================

/**
 * Cleans up expired notifications
 * Can be called periodically or on app startup
 *
 * @returns Promise<number> - Number of notifications cleaned up
 */
export async function cleanupExpiredNotifications(): Promise<number> {
  try {
    const { data, error } = await supabase.rpc('cleanup_expired_notifications');

    if (error) {
      console.error('Error cleaning up expired notifications:', error);
      return 0;
    }

    return (data as number) || 0;
  } catch (error) {
    console.error('Unexpected error cleaning up expired notifications:', error);
    return 0;
  }
}

/**
 * Cleans up old locally dismissed notifications (older than 30 days)
 */
export function cleanupOldDismissedNotifications(): void {
  if (typeof window === 'undefined') return;

  try {
    const dismissed = localStorage.getItem(DISMISSED_NOTIFICATIONS_KEY);
    if (!dismissed) return;

    // Simply clear all - they're dismissed for a reason
    // and IDs won't repeat
    localStorage.removeItem(DISMISSED_NOTIFICATIONS_KEY);
  } catch (error) {
    console.error('Error cleaning up old dismissed notifications:', error);
  }
}
